/**
 * Toast Component and Context
 *
 *   showToast('success', '저장되었습니다.');                       // 기본 (1.5초 후 사라짐)
 *
 * ── 진행 → 결과 (같은 id 로 다시 부르면 그 자리에서 교체) ──
 *   const id = showToast('loading', '연결 테스트 중...', { title: 'labon' });   // 스피너, 자동으로 안 사라짐
 *   showToast('success', '연결이 정상입니다.', { id });
 *   showToast('error', '', { id, title: 'labon 연결 실패', detail: err.message });
 *
 * - type: success | error | info | loading
 * - options.title: 메시지 앞 굵은 글씨 (이름 등)
 * - options.detail: 아래 회색 코드 박스 (에러 원문 등) - 있으면 5초 동안 표시
 * - options.duration: 표시 시간(ms) 직접 지정 / loading 은 결과로 교체될 때까지 유지
 * - 스타일: index.css "Toast"
 */

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import InfoIcon from '../../assets/icons/common/info.svg?react';
import CheckIcon from '../../assets/icons/common/check.svg?react';
import CloseIcon from '../../assets/icons/common/close.svg?react';

type ToastType = 'success' | 'error' | 'info' | 'loading';

interface ToastOptions {
  /** 같은 id 의 토스트가 떠 있으면 교체 (진행 중 → 결과) */
  id?: string;
  title?: string;
  detail?: string;
  duration?: number;
}

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  detail?: string;
}

interface ToastContextType {
  toasts: Toast[];
  /** 반환값: 토스트 id (결과로 교체할 때 사용) */
  showToast: (type: ToastType, message: string, options?: ToastOptions) => string;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

const DEFAULT_DURATION = 1500;
const DETAIL_DURATION = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // 토스트별 자동 닫힘 타이머 - 교체 시 이전 타이머가 새 토스트를 지우지 않도록 관리
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const hideToast = useCallback((id: string) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((type: ToastType, message: string, options: ToastOptions = {}) => {
    const id = options.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const toast: Toast = { id, type, message, title: options.title, detail: options.detail };

    setToasts((prev) =>
      prev.some((t) => t.id === id)
        ? prev.map((t) => (t.id === id ? toast : t))
        : [...prev, toast]
    );

    clearTimeout(timers.current[id]);
    delete timers.current[id];
    const duration = options.duration ?? (type === 'loading' ? 0 : options.detail ? DETAIL_DURATION : DEFAULT_DURATION);
    if (duration > 0) {
      timers.current[id] = setTimeout(() => hideToast(id), duration);
    }
    return id;
  }, [hideToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, hideToast }}>
      {children}
      <ToastContainer toasts={toasts} onClose={hideToast} />
    </ToastContext.Provider>
  );
}

const ICONS: Record<Exclude<ToastType, 'loading'>, typeof InfoIcon> = {
  success: CheckIcon,
  error: CloseIcon,
  info: InfoIcon,
};

function ToastContainer({ toasts, onClose }: { toasts: Toast[]; onClose: (id: string) => void }) {
  return (
    <div className="toast-container">
      {toasts.map((toast) => {
        const Icon = toast.type === 'loading' ? null : ICONS[toast.type];
        return (
          <div
            key={toast.id}
            className={`toast ${toast.type}`}
            role={toast.type === 'error' ? 'alert' : 'status'}
            onClick={() => onClose(toast.id)}
          >
            <span className="toast-icon">
              {Icon ? <Icon /> : <span className="toast-spinner" />}
            </span>
            <div className="toast-content">
              <div className="toast-message">
                {toast.title && <strong>{toast.title}</strong>}
                {toast.title && toast.message && ' '}
                {toast.message}
              </div>
              {toast.detail && <pre className="toast-detail">{toast.detail}</pre>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

export default ToastProvider;
